import { useState, useEffect, useCallback } from 'react'
import { Document } from '@/lib/db/schema'

export function useDocuments(businessId: string | undefined) {
  const [documents, setDocuments] = useState<Document[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchDocuments = useCallback(async () => {
    if (!businessId) {
      setIsLoading(false)
      return
    }
    setIsLoading(true)
    setError(null)
    try {
      const res = await fetch(`/api/businesses/${businessId}/documents`)
      const json = await res.json() as any
      if (res.ok && json.success) {
        setDocuments(json.data)
      } else {
        setError(json.error?.message || 'Failed to fetch documents')
      }
    } catch (err: any) {
      setError(err.message || 'Network error')
    } finally {
      setIsLoading(false)
    }
  }, [businessId])

  useEffect(() => {
    fetchDocuments()
  }, [fetchDocuments])

  const uploadDocument = async (file: File, documentType: string, notes: string) => {
    if (!businessId) return { success: false, error: 'No business ID' }
    
    const formData = new FormData()
    formData.append('file', file)
    formData.append('documentType', documentType)
    formData.append('notes', notes)

    const res = await fetch(`/api/businesses/${businessId}/documents`, {
      method: 'POST',
      body: formData // No Content-Type header needed for FormData
    })
    
    const json = await res.json() as any
    if (res.ok && json.success) {
      fetchDocuments()
      return { success: true, data: json.data }
    }
    return { success: false, error: json.error?.message || 'Upload failed' }
  }

  const getDownloadUrl = (docId: string) => {
    return `/api/businesses/${businessId}/documents/${docId}/download`
  }

  return { documents, isLoading, error, refetch: fetchDocuments, uploadDocument, getDownloadUrl }
}
